import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { Tokens } from "../identity/auth.routes.js";
import { DEFAULT_OPERATOR_ROLES, OPERATOR_PERMISSIONS } from "../permissions.js";
const Email = z.email().max(254);
const OneTimeCode = z.string().regex(/^\d{6}$/);
const OperatorRoleKey = z
    .enum(Object.keys(DEFAULT_OPERATOR_ROLES))
    .meta({ id: "OperatorRole" });
const Ops = { surface: "operator" };
/** Operators always need TOTP: the first sign-in enrols it, every later one asks for it (ADR 0015). */
export const OperatorSignInStep = z
    .discriminatedUnion("status", [
    z.object({ status: z.literal("mfa_required"), challengeId: z.string() }),
    z.object({
        status: z.literal("mfa_enrolment_required"),
        challengeId: z.string(),
        otpauthUri: z.string().startsWith("otpauth://totp/"),
        secret: z.string(),
    }),
])
    .meta({ id: "OperatorSignInStep" });
export const opsEmailStart = defineRoute({
    ...Ops,
    id: "ops.auth.email.start",
    method: "POST",
    path: "/v1/ops/auth/email/start",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Send a sign-in code to an operator's email; unknown or disabled addresses get nothing, silently",
    prd: ["ADM-1"],
    request: { body: z.object({ email: Email }) },
    responses: { 204: { description: "Code sent if the address belongs to an active operator" } },
    errors: ["VALIDATION_FAILED", "RATE_LIMITED"],
});
export const opsEmailVerify = defineRoute({
    ...Ops,
    id: "ops.auth.email.verify",
    method: "POST",
    path: "/v1/ops/auth/email/verify",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Check the code; the answer is always a TOTP step, enrolment on the first sign-in",
    prd: ["ADM-1"],
    request: { body: z.object({ email: Email, code: OneTimeCode }) },
    responses: { 200: { description: "The TOTP step", schema: OperatorSignInStep } },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "RATE_LIMITED"],
});
export const opsMfaVerify = defineRoute({
    ...Ops,
    id: "ops.auth.mfa.totp.verify",
    method: "POST",
    path: "/v1/ops/auth/mfa/totp/verify",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Complete operator sign-in with a TOTP code, which also confirms enrolment; the session lasts 12 hours",
    prd: ["ADM-1"],
    request: { body: z.object({ challengeId: z.string().min(1), code: OneTimeCode }) },
    responses: {
        200: {
            description: "Signed in; the browser gets the refresh token as a cookie",
            schema: z.object({ operatorId: Id, tokens: Tokens }),
        },
    },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "RATE_LIMITED"],
});
export const opsRefresh = defineRoute({
    ...Ops,
    id: "ops.auth.refresh",
    method: "POST",
    path: "/v1/ops/auth/refresh",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Rotate the operator refresh token; the session never outlives 12 hours from sign-in",
    request: {
        body: z.object({
            refreshToken: z.string().optional().describe("Tools only; the console sends the cookie"),
        }),
    },
    responses: { 200: { description: "New tokens", schema: Tokens } },
    errors: ["AUTH_REQUIRED", "TOKEN_EXPIRED"],
});
export const opsLogout = defineRoute({
    ...Ops,
    id: "ops.auth.logout",
    method: "POST",
    path: "/v1/ops/auth/logout",
    auth: "operator",
    permission: "none",
    marks: [],
    summary: "End this operator session at once",
    responses: { 204: { description: "Signed out" } },
    errors: ["AUTH_REQUIRED"],
});
export const opsStepUp = defineRoute({
    ...Ops,
    id: "ops.auth.step-up",
    method: "POST",
    path: "/v1/ops/auth/step-up",
    auth: "operator",
    permission: "none",
    marks: [],
    summary: "Take a TOTP code and return an access token elevated for 5 minutes",
    request: { body: z.object({ code: OneTimeCode }) },
    responses: { 200: { description: "Elevated access token", schema: Tokens } },
    errors: ["AUTH_REQUIRED", "INVALID_CODE", "RATE_LIMITED"],
});
export const Operator = z
    .object({
    id: Id,
    email: z.string(),
    name: z.string(),
    roles: z.array(OperatorRoleKey),
    status: z.enum(["active", "disabled"]),
    mfaEnrolled: z.boolean(),
    createdAt: Timestamp,
})
    .meta({ id: "Operator" });
export const opsMe = defineRoute({
    ...Ops,
    id: "ops.me.get",
    method: "GET",
    path: "/v1/ops/me",
    auth: "operator",
    permission: "none",
    marks: [],
    summary: "The signed-in operator with roles and permissions",
    responses: {
        200: {
            description: "The operator",
            schema: Operator.extend({ permissions: z.array(z.enum(OPERATOR_PERMISSIONS)) }),
        },
    },
    errors: ["AUTH_REQUIRED"],
});
export const listOperators = defineRoute({
    ...Ops,
    id: "ops.operators.list",
    method: "GET",
    path: "/v1/ops/operators",
    auth: "operator",
    permission: "ops:operators.manage",
    marks: [],
    summary: "Every operator account",
    prd: ["ADM-1"],
    responses: { 200: { description: "Operators", schema: z.object({ items: z.array(Operator) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const createOperator = defineRoute({
    ...Ops,
    id: "ops.operators.create",
    method: "POST",
    path: "/v1/ops/operators",
    auth: "operator",
    permission: "ops:operators.manage",
    marks: ["step-up", "idem"],
    summary: "Create an operator account; they enrol TOTP at their first sign-in",
    prd: ["ADM-1"],
    request: {
        body: z.object({
            email: Email,
            name: z.string().trim().min(1).max(100),
            roles: z.array(OperatorRoleKey).min(1),
        }),
    },
    responses: { 201: { description: "The operator", schema: Operator } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "DUPLICATE_REGISTRATION"],
});
export const updateOperator = defineRoute({
    ...Ops,
    id: "ops.operators.update",
    method: "PATCH",
    path: "/v1/ops/operators/{operatorId}",
    auth: "operator",
    permission: "ops:operators.manage",
    marks: ["step-up"],
    summary: "Rename an operator or replace their roles; the platform always keeps one active super admin",
    prd: ["ADM-1"],
    request: {
        params: z.object({ operatorId: Id }),
        body: z.object({
            name: z.string().trim().min(1).max(100).optional(),
            roles: z.array(OperatorRoleKey).min(1).optional(),
        }),
    },
    responses: { 200: { description: "The operator", schema: Operator } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const disableOperator = defineRoute({
    ...Ops,
    id: "ops.operators.disable",
    method: "POST",
    path: "/v1/ops/operators/{operatorId}/disable",
    auth: "operator",
    permission: "ops:operators.manage",
    marks: ["step-up"],
    summary: "Disable an operator; their sessions end at once",
    prd: ["ADM-1"],
    request: { params: z.object({ operatorId: Id }) },
    responses: { 200: { description: "The operator", schema: Operator } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const operatorRoutes = [
    opsEmailStart,
    opsEmailVerify,
    opsMfaVerify,
    opsRefresh,
    opsLogout,
    opsStepUp,
    opsMe,
    listOperators,
    createOperator,
    updateOperator,
    disableOperator,
];
//# sourceMappingURL=operator.routes.js.map